// Location: src/components/admin/VideoContent.tsx
// Purpose: Video content management tools for creating and managing video content
// Scope: Shows video content and allows for video content management
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader } from '@/components/ui/loader';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { formatDuration } from '@/lib/utils';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogHeader,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Check, Play, Trash, X, Plus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { DeleteIcon } from '../ui/icons/delete-icon';

// Define a Video type based on the schema.ts definition
type Video = {
	_id: Id<'videos'>;
	_creationTime: number;
	title: string;
	description?: string;
	userId: Id<'users'>;
	platform: 'youtube' | 'tiktok' | 'instagram' | 'facebook' | 'twitter';
	style: string;
	width: number;
	height: number;
	length: number;
	status: 'draft' | 'processing' | 'rendering' | 'published' | 'failed';
	finalVideoId?: Id<'media'>;
	finalVideoUrl?: string;
	coverImageUrl?: string; // Original cover image
	firstSceneImageUrl?: string | null; // Image from the first scene
	createdAt: string;
	updatedAt: string;
	metadata?: {
		tags?: string[];
		category?: string;
		visibility?: string;
		thumbnailId?: Id<'media'>;
		engagement?: {
			views?: number;
			likes?: number;
			comments?: number;
			shares?: number;
		};
		publishedAt?: string;
	};
};

export function VideoContent() {
	const { user, isSuperAdmin } = useAuth();
	const navigate = useNavigate();
	const [searchTerm, setSearchTerm] = React.useState('');
	const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);
	const [isIconHovered, setIsIconHovered] = React.useState(false);
	const [showCreateDialog, setShowCreateDialog] = React.useState(false);
	const [isCreatingUser, setIsCreatingUser] = React.useState(false);
	const [videoToDelete, setVideoToDelete] =
		React.useState<Id<'videos'> | null>(null);

	// Cache for user information to avoid repeated queries
	const [userCache, setUserCache] = React.useState<
		Record<string, { email: string; name: string }>
	>({});

	// State for new video creation
	const [newVideo, setNewVideo] = React.useState({
		title: '',
		description: '',
		platform: 'tiktok',
		style: 'standard',
		width: 1080,
		height: 1920,
		coverImageUrl: '', // Default empty URL for cover image
	});

	// State for video orientation
	const [orientation, setOrientation] = React.useState<
		'landscape' | 'portrait'
	>('portrait');

	// Use the userId from useAuth directly
	const userId = user?.id;
	const userEmail = user?.email;

	// Create video mutation with type assertion to handle API typing issues
	const createVideoMutation = useMutation(api.videos.createVideo);

	// Delete video mutation with type assertion to handle API typing issues
	const deleteVideoMutation = useMutation(api.videos.deleteVideo);

	// Function to check if user exists - simplified to avoid creating users if they don't exist
	const createUserIfNeeded = React.useCallback(async () => {
		// This function is no longer needed as useAuth handles user creation/fetching
		return userId;
	}, [userId]);

	// If no userId is available, try to create a user with the clerk user data
	useEffect(() => {
		let isMounted = true;

		const checkUser = async () => {
			if (!userId && user && !isCreatingUser && isMounted) {
				try {
					setIsCreatingUser(true);
					await createUserIfNeeded();
				} finally {
					if (isMounted) {
						setIsCreatingUser(false);
					}
				}
			}
		};

		checkUser();

		return () => {
			isMounted = false;
		};
	}, [userId, user, createUserIfNeeded, isCreatingUser]);

	// Fetch ALL videos using the modified getUserVideos function with undefined userId
	// This will return all videos regardless of owner
	const allVideos = useQuery(
		api.videos.getUserVideos,
		searchTerm.length >= 3 ? { platform: searchTerm } : {}
	);

	// Keep a reference to user's own videos for future curation features
	const userOwnVideos = useQuery(
		api.videos.getUserVideos,
		userId ? { userId } : { userId: undefined }
	);

	// Always use allVideos for display, regardless of user role
	const displayVideos = React.useMemo(() => allVideos || [], [allVideos]);

	// Improve the memoization of filteredVideos
	const filteredVideos = React.useMemo(() => {
		if (searchTerm.length < 3 && searchTerm.length > 0) {
			return displayVideos.filter((video: Video) =>
				video.title.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}
		return displayVideos;
	}, [displayVideos, searchTerm]);

	// Improve the memoization of groupedVideos
	const groupedVideos = React.useMemo(
		() => ({
			youtube:
				filteredVideos?.filter(
					(video: Video) => video.platform === 'youtube'
				) || [],
			tiktok:
				filteredVideos?.filter(
					(video: Video) => video.platform === 'tiktok'
				) || [],
			instagram:
				filteredVideos?.filter(
					(video: Video) => video.platform === 'instagram'
				) || [],
			facebook:
				filteredVideos?.filter(
					(video: Video) => video.platform === 'facebook'
				) || [],
			twitter:
				filteredVideos?.filter(
					(video: Video) => video.platform === 'twitter'
				) || [],
		}),
		[filteredVideos]
	);

	// Loading state depends on whether the videos query has returned
	const isLoading = displayVideos === undefined;

	// Function to get creator email for a video
	const getCreatorEmail = (video: Video) => {
		// For now, just return a placeholder since we don't have user lookup functionality yet
		// This will be improved in a future update when we implement proper user lookup
		return 'Video Creator';
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	// Handle form input changes
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setNewVideo((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	// Handle select changes
	const handleSelectChange = (name: string, value: string) => {
		setNewVideo((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	// Handle orientation change
	const handleOrientationChange = (
		newOrientation: 'landscape' | 'portrait'
	) => {
		setOrientation(newOrientation);

		// Update width and height based on orientation
		if (newOrientation === 'landscape') {
			setNewVideo((prev) => ({
				...prev,
				width: 1920,
				height: 1080,
			}));
		} else {
			setNewVideo((prev) => ({
				...prev,
				width: 1080,
				height: 1920,
			}));
		}
	};

	// Calculate totals directly - don't use state
	const totalVideos = filteredVideos.length;

	// Check if user is allowed to create videos (superadmin or specific email)
	const canCreateVideos = isSuperAdmin || user?.email === '';

	// Submit new video using Convex mutation
	const handleCreateVideo = async () => {
		try {
			if (!userId) {
				toast({
					title: 'Authentication Required',
					description: 'You need to be logged in to create videos',
					variant: 'destructive',
				});
				return;
			}

			// Log what's happening for debugging
			// console.log('Creating video with:', {
			// 	userId,
			// 	title: newVideo.title,
			// 	email: userEmail,
			// });

			// Set default coverImageUrl based on dimensions if not already set
			let coverImageUrl = newVideo.coverImageUrl;
			if (!coverImageUrl) {
				if (newVideo.width === 1920 && newVideo.height === 1080) {
					coverImageUrl =
						'https://watchers.sirv.com/watchers/ai/prometheus/cover1920x1080.png';
				} else if (
					newVideo.width === 1080 &&
					newVideo.height === 1920
				) {
					coverImageUrl =
						'https://watchers.sirv.com/watchers/ai/prometheus/cover1080x1920.png';
				}
			}

			// Call the Convex mutation with all fields
			const videoId = await createVideoMutation({
				title: newVideo.title,
				description: newVideo.description,
				platform: newVideo.platform as
					| 'youtube'
					| 'tiktok'
					| 'instagram'
					| 'facebook'
					| 'twitter',
				style: newVideo.style,
				width: Number(newVideo.width), // Ensure width is a number
				height: Number(newVideo.height), // Ensure height is a number
				length: 0, // Default length
				userId, // The ID from useAuth() is already the correct type
				status: 'draft',
				coverImageUrl: coverImageUrl || undefined, // Use our potentially updated coverImageUrl
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
			});

			console.log('Video created with ID:', videoId);

			toast({
				title: 'Video Created',
				description:
					'Your new video project has been created successfully.',
			});

			// Reset form and close dialog
			resetForm();
			setShowCreateDialog(false);
		} catch (error) {
			console.error('Error creating video:', error);

			// More detailed error message for development debugging
			const errorMsg =
				error instanceof Error ? error.message : String(error);
			console.log('Error details:', errorMsg);

			toast({
				title: 'Error Creating Video',
				description:
					'There was a problem creating your video. Please check console for details.',
				variant: 'destructive',
			});
		}
	};

	// Delete video
	const handleDeleteVideo = async (videoId: Id<'videos'>) => {
		try {
			if (!userId) {
				toast({
					title: 'Authentication Required',
					description: 'You need to be logged in to delete videos',
					variant: 'destructive',
				});
				return;
			}

			// Log what's happening for debugging
			// console.log('Attempting to delete video:', {
			// 	videoId,
			// 	userId,
			// 	email: userEmail,
			// 	isSuperAdmin,
			// });

			// Check if the selected video exists in our data
			const videoToDelete = displayVideos?.find(
				(v: Video) => v._id === videoId
			);

			if (!videoToDelete) {
				toast({
					title: 'Video Not Found',
					description:
						'The video you are trying to delete could not be found.',
					variant: 'destructive',
				});
				return;
			}

			// Only allow deletion if user is superadmin or owns the video
			if (
				!isSuperAdmin &&
				videoToDelete.userId.toString() !== userId.toString()
			) {
				toast({
					title: 'Permission Denied',
					description: 'You can only delete videos you created.',
					variant: 'destructive',
				});
				return;
			}

			await deleteVideoMutation({
				videoId,
				userId, // Pass the userId to the mutation
			});

			toast({
				title: 'Video Deleted',
				description: 'The video has been deleted successfully.',
			});

			// Reset the videoToDelete state
			setVideoToDelete(null);
		} catch (error) {
			console.error('Error deleting video:', error);

			// Provide more detailed error message
			const errorMsg =
				error instanceof Error ? error.message : String(error);

			toast({
				title: 'Error',
				description: `Failed to delete video: ${errorMsg}`,
				variant: 'destructive',
			});

			// Reset the videoToDelete state even if there's an error
			setVideoToDelete(null);
		}
	};

	// Get user's email for display
	const userEmailForDisplay = userEmail || 'User Email Not Available';

	// Reset form function
	const resetForm = () => {
		setSearchTerm('');
		setHoveredCard(null);
		setShowCreateDialog(false);
		setNewVideo({
			title: '',
			description: '',
			platform: 'tiktok',
			style: 'standard',
			width: 1080,
			height: 1920,
			coverImageUrl: '', // Reset cover image URL
		});
	};

	return (
		<div className='video-container mx-auto p-6 pb-80'>
			<div className='h-full flex-1 flex flex-col'>
				<div className='flex-col flex'>
					<div className='flex flex-col gap-2'>
						<div className='flex items-center justify-between py-4'>
							<div>
								<h1 className='text-3xl font-bold'>
									<span className='bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-600'>
										Video Engine
									</span>
								</h1>
								<p className='text-lg text-gray-500 mt-1 mb-6'>
									Create and manage your video content
								</p>
							</div>

							<div className='flex items-center gap-2'>
								<Input
									placeholder='Search videos...'
									value={searchTerm}
									onChange={handleSearch}
									className='w-64'
								/>
								{canCreateVideos && (
									<Button
										onClick={() =>
											setShowCreateDialog(true)
										}
										className='bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-purple-500/20'
									>
										<span className='flex items-center gap-2'>
											<span className='text-white'>
												Create New Video
											</span>
											<span className='w-4 h-4 rounded-full bg-white/20 flex items-center justify-center'>
												<Plus className='w-3 h-3 text-white' />
											</span>
										</span>
									</Button>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Video Content Card */}
			<Card className='mb-6 glow-card'>
				<CardHeader>
					<CardTitle className='pink-title text-3xl font-bold'>
						All Videos
					</CardTitle>
					<div className='space-y-2'>
						<p className='text-sm text-muted-foreground'>
							{isLoading ? (
								<Loader
									className='py-12'
									message='LOADING VIDEOS...'
								/>
							) : (
								<>
									{filteredVideos.length} video
									{filteredVideos.length !== 1 ? 's' : ''} •
									Showing global video library
								</>
							)}
						</p>
					</div>
				</CardHeader>
				<CardContent>
					{!isLoading && (
						<div className='space-y-8'>
							{/* Platform sections */}
							{Object.entries(groupedVideos).map(
								([platform, videos]) =>
									videos.length > 0 && (
										<div
											key={platform}
											className='space-y-4'
										>
											<h3
												className={`text-3xl font-bold ${
													platform === 'youtube'
														? 'text-red-500'
														: platform === 'tiktok'
															? 'text-cyan-500'
															: platform ===
																  'instagram'
																? 'text-purple-500'
																: platform ===
																	  'facebook'
																	? 'text-blue-500'
																	: 'text-sky-500'
												}`}
											>
												{platform
													.charAt(0)
													.toUpperCase() +
													platform.slice(1)}
											</h3>
											<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-16'>
												{videos?.map((video: Video) => (
													<Card
														key={String(video._id)}
														className='bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors overflow-hidden flex flex-col'
														onClick={() =>
															navigate(
																`/admin/video/timeline/${video._id}`
															)
														}
														onMouseEnter={() =>
															setHoveredCard(
																String(
																	video._id
																)
															)
														}
														onMouseLeave={() =>
															setHoveredCard(null)
														}
													>
														{/* Card image with platform-colored overlay */}
														{video.firstSceneImageUrl ||
														video.coverImageUrl ? (
															<div
																className={`relative w-full overflow-hidden ${
																	video.width >
																	video.height
																		? 'aspect-video'
																		: 'aspect-[9/16]'
																}`}
															>
																<img
																	src={
																		video.firstSceneImageUrl ??
																		video.coverImageUrl
																	}
																	alt={
																		video.title
																	}
																	className='h-full w-full object-cover'
																/>

																{/* Platform badge */}
																<div className='absolute bottom-2 right-2 text-white bg-black bg-opacity-50 px-2 py-1 rounded text-sm'>
																	{
																		video.platform
																	}
																</div>
																{/* Status badge */}
																<div className='absolute top-2 left-2 text-white bg-black bg-opacity-50 px-2 py-1 rounded text-sm'>
																	{
																		video.status
																	}
																</div>
																{/* Resolution badge */}
																<div className='absolute top-2 right-2 text-foreground bg-black bg-opacity-70 px-2 py-1 rounded text-sm'>
																	{
																		video.style
																	}
																</div>
															</div>
														) : (
															/* Placeholder colored rectangle if no cover image */
															<div
																className={`w-full relative flex items-center justify-center ${
																	video.width >
																	video.height
																		? 'aspect-video'
																		: 'aspect-[9/16]'
																} ${
																	video.platform ===
																	'youtube'
																		? 'bg-red-100'
																		: video.platform ===
																			  'tiktok'
																			? 'bg-cyan-100'
																			: video.platform ===
																				  'instagram'
																				? 'bg-purple-100'
																				: video.platform ===
																					  'facebook'
																					? 'bg-blue-100'
																					: 'bg-sky-100'
																}`}
															>
																<span className='text-lg font-semibold'>
																	{video.title
																		.substring(
																			0,
																			1
																		)
																		.toUpperCase()}
																</span>
																{/* Resolution badge */}
																<div className='absolute top-2 right-2 text-foreground bg-white bg-opacity-70 px-2 py-1 rounded text-sm'>
																	{
																		video.style
																	}
																</div>
															</div>
														)}
														<CardContent className='p-4'>
															<div className='flex justify-between items-center'>
																<div>
																	<h4 className='text-lg font-semibold'>
																		{
																			video.title
																		}
																	</h4>
																</div>
																<div className='flex gap-2'>
																	{(isSuperAdmin ||
																		(userId &&
																			video.userId.toString() ===
																				userId.toString())) && (
																		<div
																			className='cursor-pointer hover:bg-accent rounded-md transition-colors'
																			onClick={(
																				e
																			) => {
																				e.stopPropagation();
																				setVideoToDelete(
																					video._id
																				);
																			}}
																		>
																			<DeleteIcon
																				isHovered={
																					hoveredCard ===
																					String(
																						video._id
																					)
																				}
																			/>
																		</div>
																	)}
																</div>
															</div>
														</CardContent>
													</Card>
												))}
											</div>
										</div>
									)
							)}

							{/* Only show "No videos found" if total count across all platforms is zero */}
							{totalVideos === 0 && (
								<div className='text-center py-8'>
									<p className='text-lg text-gray-500'>
										No videos found. Create your first video
										by clicking the button above.
									</p>
								</div>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Create Video Dialog */}
			<Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
				<DialogContent className='sm:max-w-[800px]'>
					<DialogHeader>
						<DialogTitle className='text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600'>
							Create New Video
						</DialogTitle>
						<DialogDescription>
							Enter the details for your new video project.
						</DialogDescription>
					</DialogHeader>
					<div className='grid gap-4 py-4'>
						<div className='grid gap-2'>
							<Label htmlFor='title'>Title</Label>
							<Input
								id='title'
								name='title'
								value={newVideo.title}
								onChange={handleInputChange}
								placeholder='Enter video title'
							/>
						</div>
						<div className='grid gap-2'>
							<Label htmlFor='description'>Description</Label>
							<Input
								id='description'
								name='description'
								value={newVideo.description}
								onChange={handleInputChange}
								placeholder='Enter video description'
							/>
						</div>
						<div className='grid gap-2'>
							<Label htmlFor='coverImageUrl'>
								Cover Image URL
							</Label>
							<Input
								id='coverImageUrl'
								name='coverImageUrl'
								value={newVideo.coverImageUrl}
								onChange={handleInputChange}
								placeholder='https://example.com/image.jpg'
							/>
							<p className='text-xs text-muted-foreground'>
								Provide a URL to an image that will be used as
								the video cover
							</p>
						</div>
						<div className='grid gap-2'>
							<Label htmlFor='platform'>Platform</Label>
							<Select
								defaultValue={newVideo.platform}
								onValueChange={(value) =>
									handleSelectChange('platform', value)
								}
							>
								<SelectTrigger>
									<SelectValue placeholder='Select platform' />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='youtube'>
										YouTube
									</SelectItem>
									<SelectItem value='tiktok'>
										TikTok
									</SelectItem>
									<SelectItem value='instagram'>
										Instagram
									</SelectItem>
									<SelectItem value='facebook'>
										Facebook
									</SelectItem>
									<SelectItem value='twitter'>
										Twitter
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className='grid gap-2'>
							<Label htmlFor='style'>Style</Label>
							<Select
								defaultValue={newVideo.style}
								onValueChange={(value) =>
									handleSelectChange('style', value)
								}
							>
								<SelectTrigger>
									<SelectValue placeholder='Select style' />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='standard'>
										Standard
									</SelectItem>
									<SelectItem value='cinematic'>
										Cinematic
									</SelectItem>
									<SelectItem value='animated'>
										Animated
									</SelectItem>
									<SelectItem value='documentary'>
										Documentary
									</SelectItem>
									<SelectItem value='tutorial'>
										Tutorial
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className='grid gap-2'>
							<Label htmlFor='orientation'>Orientation</Label>
							<div className='flex items-center justify-center gap-6 my-2'>
								<div
									className={`w-36 cursor-pointer transition-all duration-200 rounded-xl ${
										orientation === 'landscape'
											? 'shadow-[0_0_12px_rgba(236,72,153,0.7)] scale-110'
											: 'opacity-70 hover:opacity-100'
									}`}
									onClick={() =>
										handleOrientationChange('landscape')
									}
								>
									<div className='aspect-video bg-gradient-to-br from-zinc-800 to-zinc-950 border border-zinc-700 rounded-xl overflow-hidden shadow-md'>
										<div className='p-2 h-full flex flex-col justify-between'>
											<div className='w-full flex justify-between'>
												<div className='w-1/3 h-1 bg-pink-500/80 rounded-full'></div>
												<div className='w-1/6 h-1 bg-pink-500/50 rounded-full'></div>
											</div>
											<div className='text-center'>
												<div className='text-xs font-medium'>
													Landscape
												</div>
												<div className='text-[10px] text-zinc-400'>
													1920×1080
												</div>
											</div>
										</div>
									</div>
								</div>

								<div
									className={`w-24 cursor-pointer transition-all duration-200 rounded-xl ${
										orientation === 'portrait'
											? 'shadow-[0_0_12px_rgba(236,72,153,0.7)] scale-110'
											: 'opacity-70 hover:opacity-100'
									}`}
									onClick={() =>
										handleOrientationChange('portrait')
									}
								>
									<div className='aspect-[9/16] bg-gradient-to-br from-zinc-800 to-zinc-950 border border-zinc-700 rounded-xl overflow-hidden shadow-md'>
										<div className='p-2 h-full flex flex-col justify-between'>
											<div className='w-full flex justify-between'>
												<div className='w-1/3 h-1 bg-pink-500/80 rounded-full'></div>
												<div className='w-1/6 h-1 bg-pink-500/50 rounded-full'></div>
											</div>
											<div className='text-center text-[10px]'>
												<div className='font-medium'>
													Portrait
												</div>
												<div className='text-zinc-400 text-[8px]'>
													1080×1920
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant='outline'
							onClick={() => setShowCreateDialog(false)}
							className='hover:bg-zinc-900'
						>
							Cancel
						</Button>
						{isSuperAdmin || (
							<Button
								onClick={handleCreateVideo}
								disabled={!newVideo.title}
								className='bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-purple-500/20'
							>
								<span className='flex items-center gap-2'>
									<span className='text-white'>
										Create Video
									</span>
									<Plus className='w-4 h-4 text-white' />
								</span>
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Add Confirmation Dialog for video deletion */}
			<ConfirmDialog
				isOpen={videoToDelete !== null}
				onClose={() => setVideoToDelete(null)}
				onConfirm={() => {
					if (videoToDelete) {
						handleDeleteVideo(videoToDelete);
					}
				}}
				title='Delete Video'
				description='Are you sure you want to delete this video? This action cannot be undone.'
				confirmText='Delete'
				cancelText='Cancel'
				isDeleteDialog={true}
			/>
		</div>
	);
}
